import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./ProjectSection.css";
import AOS from "aos";
import "aos/dist/aos.css";
// Import AuroraText 
import { AuroraText } from "./ui/AuroraText";
import StarTrail from "./ui/StarTrail";

const ProjectSection = () => {
  const navigate = useNavigate();
  const topRef = useRef(null);
  const headerRef = useRef(null);
  const moveSparkRef = useRef(null);
  const neuroGlintRef = useRef(null);
  const patrimonioRef = useRef(null);
  const carouselMoveRef = useRef(null);
  const carouselNeuroRef = useRef(null);
  const carouselPatrimonioRef = useRef(null);
  const moveSentinelRef = useRef(null);
  const neuroSentinelRef = useRef(null);
  const patrimonioSentinelRef = useRef(null);

  const [moveAtEnd, setMoveAtEnd] = useState(false);
  const [neuroAtEnd, setNeuroAtEnd] = useState(false);
  const [patrimonioAtEnd, setPatrimonioAtEnd] = useState(false);

  useEffect(() => {
    const options = {
      root: null,
      threshold: 0.1,
      rootMargin: "0px"
    };

    const observerMove = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setMoveAtEnd(true);
      } else {
        setMoveAtEnd(false);
      }
    }, { ...options, root: carouselMoveRef.current });

    const observerNeuro = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setNeuroAtEnd(true);
      } else {
        setNeuroAtEnd(false);
      }
    }, { ...options, root: carouselNeuroRef.current });

    const observerPatrimonio = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setPatrimonioAtEnd(true);
      } else {
        setPatrimonioAtEnd(false);
      }
    }, { ...options, root: carouselPatrimonioRef.current });

    if (moveSentinelRef.current) observerMove.observe(moveSentinelRef.current);
    if (neuroSentinelRef.current) observerNeuro.observe(neuroSentinelRef.current);
    if (patrimonioSentinelRef.current) observerPatrimonio.observe(patrimonioSentinelRef.current);

    return () => {
      observerMove.disconnect();
      observerNeuro.disconnect();
      observerPatrimonio.disconnect();
    };
  }, []);

  const moveSparkData = [
    {
      id: 1,
      title: "Investigación",
      img: "/img/movespark_Investigacion.jpg",
      etiqueta: "MoveSpark | Ciencia",
      resumen: (
        <>
          <strong>Tecnología inmersiva al servicio de la salud cerebral</strong><br />
          En MoveSpark Lab investigamos cómo los videojuegos pueden convertirse en <strong>herramientas terapéuticas</strong>, con el objetivo de desarrollar soluciones que no solo acompañen <strong>procesos de rehabilitación</strong>, sino que también permitan la detección precoz de <strong>enfermedades neurodegenerativas como el Alzheimer</strong>.
        </>
      )
    },
    {
      id: 2,
      title: "Healthtech",
      img: "/img/movespark_Healthtech.png",
      etiqueta: "MoveSpark | Avance tecnológico",
      resumen: (
        <>
          <strong>Innovación con base científica</strong><br />
          Aunque nuestro objetivo es desarrollar soluciones transformadoras en el <strong>ámbito healthtech</strong>, somos plenamente conscientes de que la <strong>creación de un producto sólido y eficaz exige una base científica rigurosa</strong>. Por eso, MoveSpark Lab representa nuestro compromiso con la investigación aplicada, <strong>la validación de hipótesis y la generación de conocimiento</strong> como paso imprescindible antes de cualquier desarrollo comercial.
        </>
      )
    },
    {
      id: 3,
      title: "Hiperpersonalización",
      img: "/img/Movespark_Hiperpersonalizacion.png",
      etiqueta: "MoveSpark | Mejora de la calidad de vida",
      resumen: (
        <>
          <strong>Terapias inteligentes adaptadas al perfil de cada persona</strong><br />
          A través de la <strong>hiperpersonalización</strong>, nuestro objetivo principal es <strong>adaptar los procesos de rehabilitación</strong> utilizando datos en tiempo real y técnicas de aprendizaje automático. Lo que nos permite <strong>maximizar la eficacia terapéutica</strong> en función del perfil y la evolución de cada persona usuaria, asegurando que nuestros resultados científicos y futuros desarrollos se basen en <strong>evidencia personalizada y de alto impacto</strong>.
        </>
      )
    },
    {
      id: 4,
      title: "Agrotech",
      img: "/img/agrotech_cultivo.png",
      etiqueta: "MoveSpark | Tecnología para el campo",
      resumen: (
        <>
          <strong>Agricultura inteligente</strong><br />
          Exploramos cómo las tecnologías inmersivas, la IA y las ciencias ómicas pueden contribuir al <strong>futuro de la agricultura</strong>. Aplicamos modelos predictivos y sistemas interactivos para estudiar <strong>la salud del suelo, la valorización de residuos agrícolas y la optimización de cultivos</strong> en entornos insulares como Canarias. Nuestro enfoque combina sostenibilidad, digitalización y conocimiento científico para impulsar una <strong>agricultura más eficiente, respetuosa y resiliente</strong>.
        </>
      )
    }
  ];

  const neuroGlintData = [
    {
      id: 1,
      title: <strong>Análisis emocional</strong>,
      alt: "Análisis emocional",
      img: "/img/neuroglint_AnalisisEmocional.png",
      etiqueta: "Neuroglint | Neurociencia",
      resumen: (
        <>
          La emoción como dato clave<br />
          Capturamos <span style={{ fontWeight: 500 }}>patrones de mirada</span>, <span style={{ fontWeight: 500 }}>expresiones faciales</span> y <span style={{ fontWeight: 500 }}>respuestas fisiológicas</span> mediante IA, obteniendo datos que revelan cómo se sienten y reaccionan las personas usuarias ante estímulos concretos.
        </>
      )
    },
    {
      id: 2,
      title: <strong>Realidad Virtual</strong>,
      alt: "Realidad Virtual",
      img: "/img/neuroglin_realidadvirtual.png",
      etiqueta: "Neuroglint | Investigación",
      resumen: (
        <>
          Experimentación inmersiva y precisa<br />
          Creamos <span style={{ fontWeight: 500 }}>entornos virtuales realistas</span> para analizar percepciones, emociones y comportamientos en escenarios simulados, facilitando estudios de experiencia y comportamiento del consumidor.
        </>
      )
    },
    {
      id: 3,
      title: ( <><strong>Decisiones estratégicas basadas <br />en la ciencia</strong></>),
      alt: "Decisiones estratégicas basadas en la ciencia",
      img: "/img/neuroglint_DecisionesEstrategicas.png",
      etiqueta: "Neuroglint | Inteligencia Artificial",
      resumen: (
        <>
          Información en tiempo real para liderar con inteligencia<br />
          Ofrecemos <span style={{ fontWeight: 500 }}>análisis detallados del comportamiento humano</span>, permitiendo a organizaciones optimizar productos, servicios y campañas con una base empírica sólida.
        </>
      )
    }
  ];

  const patrimonioVivoData = [
    {
      id: 1,
      title: <strong>Guardianes y guardianas de recuerdos</strong>,
      alt: "Guardianes y guardianas de recuerdos",
      img: "/img/patrimoniovivo_guardiana-historias.png",
      etiqueta: "Patrimonio Vivo | Memoria viva",
      resumen: (
        <>
          Nuestros mayores guardan historias que no siempre están escritas. Patrimonio Vivo crea un espacio donde compartirlas y conservarlas, especialmente cuando el envejecimiento, el deterioro cognitivo o la demencia pueden hacer que esos recuerdos sean cada vez más difíciles de recuperar.
        </>
      )
    },
    {
      id: 2,
      title: <strong>Un mapa lleno de recuerdos</strong>,
      alt: "Un mapa lleno de recuerdos",
      img: "/img/patrimoniovivo_mapa.png",
      etiqueta: "Patrimonio Vivo | Exploración",
      resumen: (
        <>
          Cada punto del mapa guarda una historia. Fotografías, voces, lugares, recetas, juegos, fiestas y oficios se conectan con el territorio para que los recuerdos puedan volver a encontrarse, compartirse y permanecer.
        </>
      )
    },
    {
      id: 3,
      title: <strong>Recordar juntos</strong>,
      alt: "Recordar juntos",
      img: "/img/patrimoniovivo_explorar.png",
      etiqueta: "Patrimonio Vivo | Intergeneracional",
      resumen: (
        <>
          Una fotografía, una receta o un lugar pueden abrir la puerta a una historia. Patrimonio Vivo propone espacios de encuentro entre generaciones para conversar, recordar y transmitir aquello que forma parte de nuestra identidad.
        </>
      )
    },
    {
      id: 4,
      title: <strong>Recuerdos que pasan de generación en generación</strong>,
      alt: "Recuerdos que pasan de generación en generación",
      img: "/img/patrimoniovivo_colaboracion-escolar.png",
      etiqueta: "Patrimonio Vivo | Educación",
      resumen: (
        <>
          Alumnado y docentes recuperan historias, fotografías y testimonios de sus familias y comunidades, ayudando a conservar una memoria que, de otro modo, podría perderse.
        </>
      )
    }
  ];

  useEffect(() => {
    const container = document.querySelector('.carousel-container');
    if (container) {
      container.scrollLeft = 0;
    }
  }, []);
  
  useEffect(() => {
    AOS.init({
      duration: 800,
      once: true,
      disable: () => window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    });

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (carouselMoveRef.current) {
          carouselMoveRef.current.scrollLeft = 0;
        }
        if (carouselNeuroRef.current) {
          carouselNeuroRef.current.scrollLeft = 0;
        }
        if (carouselPatrimonioRef.current) {
          carouselPatrimonioRef.current.scrollLeft = 0;
        }
      });
    });
  
    setTimeout(() => window.scrollTo(0, 0), 50);
  }, []);
  
  return (
    
    <section className="project-section" ref={topRef}>
      {/* HEADER CON FONDO DE IMAGEN */}
      <div 
        className="project-header-container"
        ref={headerRef}
        data-aos="fade-up"
        loading="lazy"
      >
        <StarTrail containerRef={headerRef} />
        {/* Overlay para mejorar legibilidad */}
        
        
        {/* Contenido con z-index superior */}
        <div style={{ position: 'relative', zIndex: 2 }}>
          <div className="left-column-projects">
            <h1 className="project-soluciones">
              Soluciones reales para transformar la sociedad con impacto positivo.
            </h1>
            <p className="project-description">
            Tecnología aplicada con base científica.
Convertimos investigación en soluciones reales mediante neurociencia, inteligencia artificial y videojuegos, acompañando a instituciones desde el análisis hasta la implementación.
          </p>
          </div>
          <Link
            to="/mas-alla-del-id"
            className="lit-up-button"
            data-aos="zoom-in"
            data-aos-delay="600"
            data-aos-duration="800"
            data-aos-easing="ease-in-out"
            data-aos-once="true"
            style={{ marginTop: '25px' }}
          >
            <div className="lit-up-border" />
            <div className="lit-up-content">
              Más allá del I+D+i
            </div>
          </Link>
          
        </div>
      </div>

      {/* Patrimonio Vivo Section */}
      <section className="unified-section" ref={patrimonioRef}>
        <div className="neuroglint-fixed-title" data-aos="fade-right">
          <h2><AuroraText>Patrimonio Vivo</AuroraText></h2>
        </div>

        <div className="neuroglint-right">
          <div className="carousel-nav">
            <button onClick={() => carouselPatrimonioRef.current.scrollBy({ left: -320, behavior: 'smooth' })} aria-label="Ver tarjetas anteriores de Patrimonio Vivo">
              <FaChevronLeft aria-hidden="true" />
            </button>
            <button onClick={() => carouselPatrimonioRef.current.scrollBy({ left: 320, behavior: 'smooth' })} aria-label="Ver más tarjetas de Patrimonio Vivo">
              <FaChevronRight aria-hidden="true" />
            </button>
          </div>

          <div className="carousel-scene" data-aos="fade-left">
            <div className={`carousel-container ${patrimonioAtEnd ? 'is-at-end' : ''}`} ref={carouselPatrimonioRef} role="region" aria-label="Tarjetas de Patrimonio Vivo">
              <div className="text-slide">
                <p>
                  <strong>Para que los recuerdos no se pierdan</strong><br />
                  Patrimonio Vivo nace para recuperar, preservar y compartir recuerdos que forman parte de nuestra historia. Una plataforma colaborativa donde personas mayores, familias, jóvenes y centros educativos pueden conservar historias, fotografías, tradiciones y experiencias antes de que el paso del tiempo o la pérdida de memoria las haga desaparecer.
                </p>
                <div className="button-group-cta">
                  <button className="lit-up-button" onClick={() => window.open("https://patrimoniovivo.sparklingtech.es/", "_blank")} aria-label="Ver más sobre Patrimonio Vivo (se abre en una pestaña nueva)">
                    <div className="lit-up-border" />
                    <div className="lit-up-content">
                      Ver más sobre Patrimonio Vivo
                    </div>
                  </button>
                </div>
              </div>

              {patrimonioVivoData.map((item) => (
                <div className="gradient-card carousel-slide" key={item.id}>
                  <div className="gradient-card-content">
                    <div className="card-image">
                      <img src={item.img} alt={item.alt} loading="lazy"/>
                    </div>
                    <div className="card-content">
                      <span className="card-category">{item.etiqueta}</span>
                      <h3>{item.title}</h3>
                      <p>{item.resumen}</p>
                    </div>
                  </div>
                </div>
              ))}

              <div ref={patrimonioSentinelRef} style={{ minWidth: 1, width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}></div>
            </div>
          </div>
        </div>
      </section>

      {/* ADAIA Section (formerly MoveSpark) */}
      <section className="unified-section" ref={moveSparkRef}>
        <div className="movespark-fixed-title" data-aos="fade-right">
          <h2><AuroraText>ADAIA</AuroraText></h2>
        </div>

        <div className="movespark-right">
          <div className="carousel-nav">
            <button onClick={() => carouselMoveRef.current.scrollBy({ left: -320, behavior: 'smooth' })} aria-label="Ver tarjetas anteriores de ADAIA">
              <FaChevronLeft aria-hidden="true" />
            </button>
            <button onClick={() => carouselMoveRef.current.scrollBy({ left: 320, behavior: 'smooth' })} aria-label="Ver más tarjetas de ADAIA">
              <FaChevronRight aria-hidden="true" />
            </button>
          </div>

          <div className="carousel-scene" data-aos="fade-left">
            <div className={`carousel-container ${moveAtEnd ? 'is-at-end' : ''}`} ref={carouselMoveRef} role="region" aria-label="Tarjetas de ADAIA">
              <div className="carousel-slide text-slide">
                <p>
                  <strong>Inteligencia Artificial Emocional</strong> <br />
                  ADAIA utiliza inteligencia artificial diseñada para comprender el contexto emocional del usuario y responder de forma cuidadosa, ética y responsable. Combina modelos avanzados de IA con principios psicológicos validados para ofrecer un acompañamiento emocional útil, coherente y respetuoso.
                </p>
                <div className="button-group-cta">
                  <button className="lit-up-button" onClick={() => window.open("https://adaia.sparklingtech.es/", "_blank")} aria-label="Ver más sobre ADAIA (se abre en una pestaña nueva)">
                    <div className="lit-up-border" />
                    <div className="lit-up-content">
                      Ver más sobre ADAIA
                    </div>
                  </button>
                </div>
              </div>

              <div className="gradient-card carousel-slide">
                <div className="gradient-card-content">
                  <div className="card-image">
                    <img src="/img/Imagenes_web-_1_-part-1.png" alt="ADAIA AI" loading="lazy"/>
                  </div>
                  <div className="card-content">
                    <span className="card-category">ADAIA | Acompañamiento</span>
                    <h3><strong>Acompañamiento</strong></h3>
                    <p>Un espacio seguro donde expresar lo que sientes sin juicios. ADAIA te acompaña con una escucha empática y respetuosa, disponible cuando la necesites. Ofrece orientaciones basadas en enfoques psicológicos validados, ajustadas a tus emociones.</p>
                  </div>
                </div>
              </div>

              <div className="gradient-card carousel-slide">
                <div className="gradient-card-content">
                  <div className="card-image">
                    <img src="/img/Imagenes_web-_1_-part-2.png" alt="ADAIA Personalization" loading="lazy"/>
                  </div>
                  <div className="card-content">
                    <span className="card-category">ADAIA | Bienestar Basado en Ciencia</span>
                    <h3><strong>Bienestar Basado en Ciencia</strong></h3>
                    <p>Las respuestas de ADAIA se apoyan en técnicas como la terapia cognitivo-conductual y la psicología social. Reflexiona, aprende y desarrolla herramientas emocionales que te ayuden a gestionar mejor tus pensamientos, emociones y relaciones en el día a día.</p>
                  </div>
                </div>
              </div>

              <div className="gradient-card carousel-slide">
                <div className="gradient-card-content">
                  <div className="card-image">
                    <img src="/img/Imagenes_web-_1_-part-4.png" alt="ADAIA Automation" loading="lazy"/>
                  </div>
                  <div className="card-content">
                    <span className="card-category">ADAIA | Disponible para Ti</span>
                    <h3><strong>Disponible para Ti</strong></h3>
                    <p>Tus conversaciones son privadas y seguras. ADAIA está diseñada para proteger tu intimidad y darte tranquilidad en cada interacción. Un apoyo emocional constante, sin horarios ni esperas. ADAIA está ahí para escucharte, de día o de noche.</p>
                  </div>
                </div>
              </div>

              <div ref={moveSentinelRef} style={{ minWidth: 1, width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}></div>
            </div>
          </div>
        </div>
      </section>

      {/* NeuroGlint Section */}
      <section className="unified-section" ref={neuroGlintRef}>
        <div className="neuroglint-fixed-title" data-aos="fade-right">
          <h2><AuroraText>NeuroGlint</AuroraText></h2>
        </div>

        <div className="neuroglint-right">
          <div className="carousel-nav">
            <button onClick={() => carouselNeuroRef.current.scrollBy({ left: -400, behavior: 'smooth' })} aria-label="Ver tarjetas anteriores de NeuroGlint">
              <FaChevronLeft aria-hidden="true" />
            </button>
            <button onClick={() => carouselNeuroRef.current.scrollBy({ left: 400, behavior: 'smooth' })} aria-label="Ver más tarjetas de NeuroGlint">
              <FaChevronRight aria-hidden="true" />
            </button>
          </div>

          <div className="carousel-scene" data-aos="fade-left">
            <div className={`carousel-container ${neuroAtEnd ? 'is-at-end' : ''}`} ref={carouselNeuroRef} role="region" aria-label="Tarjetas de NeuroGlint">
              <div className="text-slide">
                <p>
                  <strong>Emociones que se traducen en decisiones</strong><br />NeuroGlint es un sistema avanzado de neurociencia aplicada que transforma señales emocionales en
                  datos estratégicos. Utilizamos inteligencia artificial para ayudar a empresas y profesionales a tomar
                  decisiones basadas en evidencia real.
                </p>
                <div className="button-group-cta">
                  <button className="lit-up-button" onClick={() => window.open("https://neuroglint.sparklingtech.es/")} aria-label="Ver más sobre NeuroGlint (se abre en una pestaña nueva)">
                    <div className="lit-up-border" />
                    <div className="lit-up-content">
                      Ver más sobre NeuroGlint
                    </div>
                  </button>
                </div>
              </div>

              {neuroGlintData.map((item) => (
                <div className="gradient-card carousel-slide" key={item.id}>
                  <div className="gradient-card-content">
                    <div className="card-image">
                      <img src={item.img} alt={item.alt} loading="lazy"/>
                    </div>
                    <div className="card-content">
                      <span className="card-category">{item.etiqueta}</span>
                      <h3>{item.title}</h3>
                      <p>{item.resumen}</p>
                    </div>
                  </div>
                </div>
              ))}

              <div ref={neuroSentinelRef} style={{ minWidth: 1, width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}></div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white dark:bg-gray-900" data-aos="fade-up">
        <div className="py-8 px-4 mx-auto max-w-screen-xl sm:py-16 lg:px-6">
          <div className="mx-auto max-w-screen-sm text-center">
            <h2 className="cta-title font-black tracking-tight gradient-text" style={{
                fontSize: "2.5rem",
                fontWeight: 900,
                background: "linear-gradient(135deg, #6366f1 0%, #a855f1 50%, #ec4899 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                color: "transparent",
                letterSpacing: "-0.02em",
                marginBottom: "1.5rem"
            }}>Ciencia, corazón y propósito</h2>
            
            <p className="cta-subtitle text-muted-foreground font-medium leading-relaxed" style={{
                fontSize: "1.125rem",
                lineHeight: "1.6",
                color: "#4b5563",
                maxWidth: "600px",
                margin: "1rem auto 2rem auto"
            }}>Sparkling Tech Solutions crea tecnología para la transformación social. Innovamos desde el conocimiento científico y el compromiso con el bienestar de las personas.</p>
            <Link
              to="/contacto"
              className="lit-up-button"
            >
              <div className="lit-up-border" />
              <div className="lit-up-content">
                Contáctanos
              </div>
            </Link>
          </div>
        </div>
      </section>
    </section>
  );
};

export default ProjectSection;